import React from 'react';
import { ChevronLeft, ChevronRight, Clock, Trash2 } from 'lucide-react';
import { ICON_STROKE_WIDTH } from '../../constants';
import { ESCALATION_CONTACT_ROLES } from '../../constants/escalationContact.constants';
import { ROLE_SHORT_LABELS } from '../../constants/escalationMatrix.constants';
import type { EscalationContact, EscalationContactRole } from '../../types/escalationContact.types';
import type {
  MatrixLevelFieldErrors,
  MatrixLevelFormState,
} from '../../types/escalationMatrix.types';
import {
  isLevelUnsetOptional,
  parseTimerInput,
  getLevelTimerError,
} from '../../utils/escalationMatrixValidation';

interface EscalationLevelBoxProps {
  level: MatrixLevelFormState;
  contacts: EscalationContact[];
  reservedContactIds?: ReadonlySet<number>;
  fieldErrors?: MatrixLevelFieldErrors;
  canRemove: boolean;
  disabled?: boolean;
  onChange: (patch: Partial<MatrixLevelFormState>) => void;
  onRemove: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  canMoveLeft: boolean;
  canMoveRight: boolean;
}

export default function EscalationLevelBox({
  level,
  contacts,
  reservedContactIds,
  fieldErrors = {},
  canRemove,
  disabled = false,
  onChange,
  onRemove,
  onMoveLeft,
  onMoveRight,
  canMoveLeft,
  canMoveRight,
}: Readonly<EscalationLevelBoxProps>) {
  const unsetOptional = isLevelUnsetOptional(level);
  const inlineTimerError = getLevelTimerError(level);
  const timerHasError = Boolean(fieldErrors.timerMinutes || inlineTimerError);
  const roleContacts = level.role ? contacts.filter(contact => contact.role === level.role) : [];
  const roleLabel = level.role
    ? ROLE_SHORT_LABELS[level.role]
    : unsetOptional
      ? 'Not set'
      : 'Level';

  const handleRoleChange = (value: string) => {
    if (!value) {
      onChange({ role: '', contactId: null, timerMinutes: 0, isOptional: true });
      return;
    }
    const role = value as EscalationContactRole;
    const matchingContacts = contacts.filter(contact => contact.role === role);
    const keepContact =
      level.contactId != null && matchingContacts.some(contact => contact.id === level.contactId);
    onChange({
      role,
      contactId: keepContact ? level.contactId : null,
      isOptional: false,
      timerMinutes: unsetOptional ? 15 : level.timerMinutes || 15,
    });
  };

  return (
    <div
      className={[
        'esc-matrix-level',
        unsetOptional ? 'esc-matrix-level--optional' : '',
        fieldErrors.contactId || fieldErrors.role || fieldErrors.general
          ? 'esc-matrix-level--error'
          : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="esc-matrix-level-top">
        <span
          className={[
            'esc-matrix-level-index',
            unsetOptional ? 'esc-matrix-level-index--optional' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {level.levelNumber}
        </span>
        <span className="esc-matrix-level-role-label">{roleLabel}</span>
        <div className="esc-matrix-level-actions">
          {canMoveLeft && (
            <button
              type="button"
              className="esc-matrix-level-action"
              disabled={disabled}
              onClick={onMoveLeft}
              aria-label="Move left"
            >
              <ChevronLeft
                size={14}
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </button>
          )}
          {canMoveRight && (
            <button
              type="button"
              className="esc-matrix-level-action"
              disabled={disabled}
              onClick={onMoveRight}
              aria-label="Move right"
            >
              <ChevronRight
                size={14}
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              className="esc-matrix-level-action esc-matrix-level-action--danger"
              disabled={disabled}
              onClick={onRemove}
              aria-label="Remove level"
            >
              <Trash2
                size={14}
                strokeWidth={ICON_STROKE_WIDTH}
              />
            </button>
          )}
        </div>
      </div>

      <label className="esc-matrix-level-field">
        <span className="esc-matrix-field-label">Role</span>
        <div className="esc-select-wrap">
          <select
            className={[
              'esc-input',
              'esc-select',
              'esc-matrix-level-select',
              fieldErrors.role ? 'esc-input--error' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            value={level.role || ''}
            disabled={disabled}
            onChange={e => handleRoleChange(e.target.value)}
          >
            {(!level.role || level.isOptional) && <option value="">— Not set —</option>}
            {ESCALATION_CONTACT_ROLES.map(roleOption => (
              <option
                key={roleOption.value}
                value={roleOption.value}
              >
                {roleOption.label}
              </option>
            ))}
          </select>
        </div>
        {fieldErrors.role && <span className="esc-field-error">{fieldErrors.role}</span>}
      </label>

      {level.role && !unsetOptional && (
        <label className="esc-matrix-level-field">
          <span className="esc-matrix-field-label">Contact</span>
          <div className="esc-select-wrap">
            <select
              className={[
                'esc-input',
                'esc-select',
                'esc-matrix-level-select',
                fieldErrors.contactId ? 'esc-input--error' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              value={level.contactId ?? ''}
              disabled={disabled}
              onChange={e => {
                const contactId = e.target.value ? Number(e.target.value) : null;
                onChange({ contactId });
              }}
            >
              <option value="">Select contact</option>
              {roleContacts.map(contact => {
                const isReserved = reservedContactIds?.has(contact.id) ?? false;
                return (
                  <option
                    key={contact.id}
                    value={contact.id}
                    disabled={isReserved}
                  >
                    {contact.name}
                  </option>
                );
              })}
            </select>
          </div>
          {fieldErrors.contactId && (
            <span className="esc-field-error">{fieldErrors.contactId}</span>
          )}
        </label>
      )}

      <div className="esc-matrix-level-field">
        <span className="esc-matrix-field-label">Acknowledgement timer</span>
        <div className="esc-matrix-timer-wrap">
          <Clock
            size={14}
            strokeWidth={ICON_STROKE_WIDTH}
            className="esc-matrix-timer-icon"
          />
          <input
            type="text"
            inputMode="numeric"
            className={[
              'esc-matrix-timer-input',
              timerHasError ? 'esc-matrix-timer-input--error' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            value={unsetOptional ? '0' : String(level.timerMinutes || '')}
            disabled={disabled || unsetOptional}
            onChange={e => onChange({ timerMinutes: parseTimerInput(e.target.value) })}
          />
          <span className="esc-matrix-timer-suffix">min to ack</span>
        </div>
      </div>

      {fieldErrors.general && <p className="esc-matrix-level-error">{fieldErrors.general}</p>}
    </div>
  );
}
